import {
  REPORT_REASONS,
  type CreateReportInput,
  type ReportReason,
  type ReportTarget,
} from '@hemmafixa/shared';
import { Button, FormField, InlinePanel, InlinePanelFooter, Textarea, cn, toast } from '@hemmafixa/ui';
import { useMutation } from '@tanstack/react-query';
import { Flag } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useI18n } from '@/i18n/context';
import { ApiError } from '@/lib/api';
import { api } from '@/lib/api-client';

interface ReportButtonProps {
  /** Opens the ReportPanel, which the caller places where it fits */
  onClick: () => void;
  /** Icon only, for message bubbles; the label is still read out */
  compact?: boolean;
  className?: string;
}

/** Starts a report of a message, proposal, company or user (P5-06). The reported side never learns who. */
export function ReportButton({ onClick, compact = false, className }: ReportButtonProps) {
  const { t } = useI18n();
  return (
    <Button
      type="button"
      variant="ghost"
      size={compact ? 'icon' : 'sm'}
      aria-label={compact ? t.report.button : undefined}
      title={compact ? t.report.button : undefined}
      className={cn('text-muted-foreground', compact && 'size-7', className)}
      onClick={onClick}
    >
      <Flag />
      {!compact && t.report.button}
    </Button>
  );
}

/** The report form, opened in place by ReportButton's caller. */
export function ReportPanel({
  target,
  targetId,
  onClose,
}: {
  target: ReportTarget;
  targetId: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const r = t.report;
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [missing, setMissing] = useState(false);
  const send = useMutation({
    mutationFn: (input: CreateReportInput) => api('/reports', { method: 'POST', body: input }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!reason) {
      setMissing(true);
      return;
    }
    send.mutate(
      { target, targetId, reason, details: details.trim() || undefined },
      {
        onSuccess: () => {
          onClose();
          toast.success(r.sentToast);
        },
        onError: (error) =>
          toast.error(error instanceof ApiError && error.status === 409 ? r.alreadyReported : r.failed),
      },
    );
  };

  return (
    <InlinePanel
      title={r.title[target]}
      description={r.text}
      onClose={onClose}
      onSubmit={submit}
      closeLabel={t.common.cancel}
    >
      <fieldset className="grid gap-2" aria-invalid={missing || undefined}>
        <legend className="mb-2 text-sm font-medium">{r.reasonLabel}</legend>
        {REPORT_REASONS.map((value) => (
          <label
            key={value}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors',
              reason === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/60',
            )}
          >
            <input
              type="radio"
              name="report-reason"
              value={value}
              checked={reason === value}
              onChange={() => {
                setReason(value);
                setMissing(false);
              }}
              className="accent-[hsl(var(--primary))]"
            />
            {r.reasons[value]}
          </label>
        ))}
        {missing && <p className="text-sm text-danger">{r.reasonRequired}</p>}
      </fieldset>
      <FormField label={r.details} hint={r.detailsHint}>
        <Textarea
          rows={3}
          maxLength={1000}
          value={details}
          onChange={(event) => setDetails(event.target.value)}
        />
      </FormField>
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" loading={send.isPending}>
          {r.send}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
